import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Checkbox,
    Colors,
    DateInput,
    Icon,
    ModalDialog,
    MultilineInput,
    Tabs,
    Text,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { createPillarSchema, DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from './validation';

import type { PillarFormData } from './validation';
import type { Pillar } from '@/types/pillars';

import { companyAtom } from '@/atoms/company';
import { userInstanceAtom } from '@/atoms/userDetails';
import ValueTranslationsTab from '@/components/AddEditValueModal/ValueTranslationsTab';
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import IconPicker from '@/components/IconPicker/IconPicker';
import StatusRadioTiles, {
    type StatusTileOption,
} from '@/components/StatusRadioTiles/StatusRadioTiles';
import PillarLeaveActiveDialog from '@/components/VisionValues/PillarLeaveActiveDialog';
import {
    PILLAR_STATUS_LABEL_KEY,
    PILLAR_STATUS_PILL,
} from '@/components/VisionValues/pillarStatusDisplay';
import { VISION_MODAL } from '@/components/VisionValues/visionModalChrome';
import { PillarStatus, type PillarStatusType } from '@/constants/PillarStatus';
import { useSelectedAppLanguage } from '@/hooks';
import i18n from '@/plugins/i18n';
import { buildGradientFromColor } from '@/utils/colorUtils';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { parseIsoDate, toDateInputLang, toIsoDate } from '@/utils/dateUtils';
import { getIconSvg } from '@/utils/iconSvgs';
import { validationMessageKey } from '@/utils/validationMessage';

export type PillarPayload = Omit<
    Pillar,
    'id' | 'createdAt' | 'updatedAt' | 'companyId' | 'sortOrder' | 'linkedObjectivesCount'
>;

interface AddEditPillarModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (pillar: PillarPayload) => Promise<void>;
    pillar?: Pillar;
    existingPillars?: Pillar[];
}

type PillarTab = 'details' | 'translations';

type TranslationsByLanguage = Record<string, Record<string, string>>;

const DEFAULT_ICON = 'target';
// Both tabs are siblings in the same row -- one size keeps their icons visually aligned instead
// of the pair silently drifting apart (`md` for one, `sm` for the other).
const TAB_ICON_SIZE = 'md';
// A new pillar starts as a draft, so creating one never runs into the active pillars limit.
const DEFAULT_STATUS = PillarStatus.DRAFT;
const STATUS_ORDER: PillarStatusType[] = [
    PillarStatus.ACTIVE,
    PillarStatus.DRAFT,
    PillarStatus.INACTIVE,
];
export default function AddEditPillarModal({
    isOpen,
    onClose,
    onSave,
    pillar,
    existingPillars = [],
}: AddEditPillarModalProps) {
    const { t } = useTranslation();
    const company = useAtomValue(companyAtom);
    const user = useAtomValue(userInstanceAtom);
    const configuredLanguages = company?.company?.availableLanguages || user?.surveyLanguages || [];
    const currentLanguage = useSelectedAppLanguage();

    const [activeTab, setActiveTab] = useState<PillarTab>('details');
    const [selectedColor, setSelectedColor] = useState<string | null>(null);
    const [initialColor, setInitialColor] = useState<string | null>(null);
    const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
    const [translations, setTranslations] = useState<TranslationsByLanguage>({});
    const [translationsValid, setTranslationsValid] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingSaveData, setPendingSaveData] = useState<PillarFormData | null>(null);
    const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);

    const isEdit = !!pillar;
    const dateLang = toDateInputLang(i18n.language);

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        trigger,
        watch,
        formState: { errors },
    } = useForm<PillarFormData>({
        resolver: zodResolver(
            createPillarSchema({
                existingPillars,
                currentPillarId: pillar?.id,
                currentLanguage,
            }),
        ),
        mode: 'onChange',
        defaultValues: {
            name: '',
            description: '',
            icon: '',
            iconBackground: '',
            status: DEFAULT_STATUS,
            periodStartDate: null,
            periodEndDate: null,
            isIndefinite: false,
        },
    });

    const periodErrorKey = validationMessageKey(
        errors.periodStartDate?.message ?? errors.periodEndDate?.message,
    );

    useEffect(() => {
        if (!isOpen) return;

        setActiveTab('details');

        if (!pillar) {
            reset({
                name: '',
                description: '',
                icon: '',
                iconBackground: '',
                status: DEFAULT_STATUS,
                periodStartDate: null,
                periodEndDate: null,
                isIndefinite: false,
            });
            setSelectedColor(null);
            setInitialColor(null);
            setSelectedIcon(null);
            setTranslations({});
            return;
        }

        reset({
            name: pillar.name?.[currentLanguage] || '',
            description: pillar.description?.[currentLanguage] || '',
            icon: pillar.icon || '',
            iconBackground: pillar.iconBackground || '',
            status: pillar.status,
            periodStartDate: pillar.periodStartDate ?? null,
            periodEndDate: pillar.periodEndDate ?? null,
            isIndefinite: !!pillar.periodStartDate && !pillar.periodEndDate,
        });

        const hexMatch = pillar.iconBackground?.match(/#([0-9a-fA-F]{6})/);
        const extractedColor = hexMatch ? `#${hexMatch[1]}` : null;
        setSelectedColor(extractedColor);
        setInitialColor(extractedColor);
        setSelectedIcon(pillar.icon || null);

        const loadedTranslations: TranslationsByLanguage = {};
        Object.entries(pillar.name ?? {}).forEach(([language, text]) => {
            if (language !== currentLanguage) {
                loadedTranslations[language] = { ...loadedTranslations[language], name: text };
            }
        });
        Object.entries(pillar.description ?? {}).forEach(([language, text]) => {
            if (language !== currentLanguage) {
                loadedTranslations[language] = {
                    ...loadedTranslations[language],
                    description: text,
                };
            }
        });
        setTranslations(loadedTranslations);
    }, [isOpen, pillar, reset, currentLanguage]);

    const buildDictionary = (
        currentValue: string,
        field: 'name' | 'description',
    ): Record<string, string> => {
        const dictionary: Record<string, string> = {};
        const trimmed = currentValue.trim();

        if (trimmed) {
            dictionary[currentLanguage] = trimmed;
        }

        Object.entries(translations).forEach(([language, fields]) => {
            const translated = fields[field]?.trim();
            if (translated) {
                dictionary[language] = translated;
            }
        });

        return dictionary;
    };

    const performSave = async (data: PillarFormData) => {
        const colorChanged = selectedColor !== initialColor;

        let iconBackground: string | undefined;
        if (selectedColor && colorChanged) {
            iconBackground = buildGradientFromColor(selectedColor);
        } else if (!colorChanged && pillar?.iconBackground) {
            iconBackground = pillar.iconBackground.trim();
        }

        setIsSaving(true);
        try {
            await onSave({
                name: buildDictionary(data.name, 'name'),
                description: buildDictionary(data.description ?? '', 'description'),
                icon: selectedIcon || data.icon?.trim() || '',
                iconBackground,
                status: data.status ?? DEFAULT_STATUS,
                periodStartDate: data.periodStartDate ?? null,
                periodEndDate: data.periodEndDate ?? null,
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Leaving Active stops offering this pillar to new objectives: confirm before an edit does
    // that, the same way the card's quick "Désactiver" action does.
    const willDeactivate = (data: PillarFormData) =>
        isEdit && pillar?.status === PillarStatus.ACTIVE && data.status !== PillarStatus.ACTIVE;

    const handleSave = async (data: PillarFormData) => {
        if (willDeactivate(data)) {
            setPendingSaveData(data);
            setIsDeactivateDialogOpen(true);
            return;
        }

        await performSave(data);
    };

    const handleCancelDeactivate = () => {
        setIsDeactivateDialogOpen(false);
        setPendingSaveData(null);
    };

    const handleConfirmDeactivate = async () => {
        if (!pendingSaveData) return;

        try {
            await performSave(pendingSaveData);
            setPendingSaveData(null);
        } finally {
            setIsDeactivateDialogOpen(false);
        }
    };

    const statusLabelId = useId();
    const statusOptions: StatusTileOption<PillarStatusType>[] = STATUS_ORDER.map((status) => ({
        value: status,
        label: t(PILLAR_STATUS_LABEL_KEY[status]),
        pill: PILLAR_STATUS_PILL[status],
    }));

    const previewGradient = selectedColor
        ? buildGradientFromColor(selectedColor)
        : resolveCompanyValueIconBackground(selectedIcon || DEFAULT_ICON, watch('iconBackground'));
    const periodStartDay = parseIsoDate(watch('periodStartDate') ?? '');
    // "Stay active indefinitely" is set explicitly, by the checkbox (or by loading a pillar that
    // was already saved that way) -- not derived from the end date on every keystroke, or setting
    // a start date first would auto-disable the end field before the user gets to it. It rides in
    // the form rather than beside it so the schema can refuse a period the box never claimed.
    const isIndefinite = watch('isIndefinite') ?? false;
    // "Indefinitely" is measured from somewhere: with no start date there is nothing for the box to
    // claim, and a pillar with no period at all is already active without end.
    const hasPeriodStart = !!watch('periodStartDate');

    const tabItems = [
        {
            key: 'details',
            label: (
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={'edit-document-text'}
                        size={TAB_ICON_SIZE}
                        colorClassName={activeTab === 'details' ? 'text-teal-500' : 'text-gray-500'}
                    />
                    {t('pillars.detailsTab')}
                </div>
            ),
            dataTestId: 'pillar-details-tab',
        },
        {
            key: 'translations',
            label: (
                <div className={'flex items-center gap-2'}>
                    <Icon
                        name={'character-translate'}
                        size={TAB_ICON_SIZE}
                        colorClassName={
                            activeTab === 'translations' ? 'text-teal-500' : 'text-gray-500'
                        }
                    />
                    {t('pillars.translationsTab')}
                </div>
            ),
            dataTestId: 'pillar-translations-tab',
        },
    ];

    return (
        <>
            <ModalDialog
                isOpen={isOpen}
                onClose={onClose}
                preventClose={isSaving}
                dataTestId={'add-edit-pillar-modal'}
                className={VISION_MODAL.panelWidth}
                zIndex={50}
                chrome={'banded'}
                radius={'2xl'}
                shadow={'2xl'}
                overlay={'medium'}
                backdropBlur
                title={isEdit ? t('pillars.editPillar') : t('pillars.addPillar')}
                subtitle={t('pillars.modalSubtitle')}
                closeAriaLabel={t('pillars.close')}
                titleIcon={
                    <div
                        className={
                            'flex h-[70px] w-[70px] items-center justify-center overflow-hidden rounded-xl shadow-lg'
                        }
                        style={{ background: previewGradient }}
                        data-testid={'pillar-icon-preview'}
                    >
                        {getIconSvg(selectedIcon || DEFAULT_ICON, 36)}
                    </div>
                }
                headerExtra={
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as PillarTab)}
                        items={tabItems}
                        className={VISION_MODAL.tabsRuleOff}
                    />
                }
                footer={
                    <>
                        <ButtonSecondary
                            onClick={onClose}
                            disabled={isSaving}
                            fitContent
                            dataTestId={'pillar-cancel'}
                        >
                            {t('pillars.cancel')}
                        </ButtonSecondary>

                        <ButtonGradientPrimary
                            onClick={handleSubmit(handleSave)}
                            disabled={
                                !watch('name')?.trim() ||
                                !translationsValid ||
                                Object.keys(errors).length > 0 ||
                                isSaving
                            }
                            loading={isSaving}
                            fitContent
                            dataTestId={'pillar-save'}
                        >
                            {isEdit ? t('pillars.save') : t('pillars.add')}
                        </ButtonGradientPrimary>
                    </>
                }
            >
                {activeTab === 'details' ? (
                    <div className={'flex flex-col gap-6'}>
                        <div className={'flex flex-col gap-2'}>
                            <label
                                htmlFor={'pillar-name'}
                                className={'text-navy text-md font-medium'}
                            >
                                {t('pillars.nameLabel')} <span className={'text-red-500'}>*</span>
                            </label>

                            <Controller
                                name={'name'}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextInput
                                        id={'pillar-name'}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        placeholder={t('pillars.namePlaceholder')}
                                        maxLength={NAME_MAX_LENGTH}
                                        showCharacterCount
                                        size={VISION_MODAL.fieldSize}
                                        textSize={VISION_MODAL.fieldTextSize}
                                        variant={'white'}
                                        bordered
                                        className={VISION_MODAL.fieldBorder}
                                        error={!!fieldState.error}
                                        errorMessage={
                                            fieldState.error?.message
                                                ? t(
                                                      validationMessageKey(
                                                          fieldState.error.message,
                                                      ),
                                                      {
                                                          max: NAME_MAX_LENGTH,
                                                      },
                                                  )
                                                : undefined
                                        }
                                        dataTestId={'pillar-name-input'}
                                    />
                                )}
                            />
                        </div>

                        <div className={'flex flex-col gap-2'}>
                            <label
                                htmlFor={'pillar-description'}
                                className={'text-navy text-md font-medium'}
                            >
                                {t('pillars.descriptionLabel')}
                            </label>

                            <Controller
                                name={'description'}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <MultilineInput
                                        id={'pillar-description'}
                                        value={field.value ?? ''}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        rows={3}
                                        placeholder={t('pillars.descriptionPlaceholder')}
                                        maxLength={DESCRIPTION_MAX_LENGTH}
                                        showCharacterCount
                                        variant={'white'}
                                        className={VISION_MODAL.fieldBorder}
                                        error={!!fieldState.error}
                                        errorMessage={
                                            fieldState.error?.message
                                                ? t(
                                                      validationMessageKey(
                                                          fieldState.error.message,
                                                      ),
                                                      {
                                                          max: DESCRIPTION_MAX_LENGTH,
                                                      },
                                                  )
                                                : undefined
                                        }
                                        dataTestId={'pillar-description-input'}
                                    />
                                )}
                            />
                        </div>

                        <div className={'flex flex-col gap-2'}>
                            <span
                                id={statusLabelId}
                                className={'text-navy text-md font-medium'}
                            >
                                {t('pillars.statusLabel')}
                            </span>

                            <Controller
                                name={'status'}
                                control={control}
                                render={({ field }) => (
                                    <StatusRadioTiles
                                        name={'pillar-status'}
                                        labelledBy={statusLabelId}
                                        options={statusOptions}
                                        value={field.value}
                                        onChange={field.onChange}
                                        dataTestId={'pillar-status'}
                                    />
                                )}
                            />
                        </div>

                        <div className={'flex flex-col gap-2'}>
                            <span className={'text-navy text-md font-medium'}>
                                {t('pillars.periodLabel')}
                            </span>

                            <div className={'flex flex-col gap-3 sm:flex-row'}>
                                <div className={'flex flex-1 flex-col gap-1.5'}>
                                    <span className={'text-sm text-gray-500'}>
                                        {t('pillars.periodStartLabel')}
                                    </span>

                                    <Controller
                                        name={'periodStartDate'}
                                        control={control}
                                        rules={{ deps: ['periodEndDate'] }}
                                        render={({ field }) => (
                                            <DateInput
                                                value={field.value ?? undefined}
                                                onChange={(date) => {
                                                    field.onChange(date ? toIsoDate(date) : null);

                                                    if (!date) {
                                                        setValue('isIndefinite', false);
                                                    }
                                                }}
                                                lang={dateLang}
                                                placeholder={t('pillars.periodStartLabel')}
                                                ariaLabel={t('pillars.periodStartLabel')}
                                                idleBorderClassName={VISION_MODAL.fieldBorder}
                                                heightClassName={VISION_MODAL.fieldHeight}
                                                clearable
                                                portalPanel
                                                dataTestId={'pillar-period-start-input'}
                                            />
                                        )}
                                    />
                                </div>

                                <div className={'flex flex-1 flex-col gap-1.5'}>
                                    <span className={'text-sm text-gray-500'}>
                                        {t('pillars.periodEndLabel')}
                                    </span>

                                    <Controller
                                        name={'periodEndDate'}
                                        control={control}
                                        rules={{ deps: ['periodStartDate'] }}
                                        render={({ field }) => (
                                            <DateInput
                                                value={field.value ?? undefined}
                                                onChange={(date) =>
                                                    field.onChange(date ? toIsoDate(date) : null)
                                                }
                                                disabledDates={
                                                    periodStartDay
                                                        ? { before: periodStartDay }
                                                        : undefined
                                                }
                                                disabled={isIndefinite}
                                                lang={dateLang}
                                                placeholder={t('pillars.periodEndLabel')}
                                                ariaLabel={t('pillars.periodEndLabel')}
                                                idleBorderClassName={VISION_MODAL.fieldBorder}
                                                heightClassName={VISION_MODAL.fieldHeight}
                                                clearable
                                                portalPanel
                                                dataTestId={'pillar-period-end-input'}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <Checkbox
                                checked={isIndefinite}
                                disabled={!hasPeriodStart}
                                onChange={(checked) => {
                                    setValue('isIndefinite', checked);

                                    if (checked) {
                                        setValue('periodEndDate', null);
                                    }

                                    void trigger(['periodStartDate', 'periodEndDate']);
                                }}
                                label={t('pillars.periodIndefinite')}
                                dataTestId={'pillar-period-indefinite-checkbox'}
                            />

                            {periodErrorKey && (
                                <div
                                    className={'mt-2 flex items-center gap-1'}
                                    role={'alert'}
                                >
                                    <Icon
                                        name={'essential-danger'}
                                        size={'sm'}
                                        color={Colors.ERROR}
                                    />

                                    <Text
                                        size={'small'}
                                        color={Colors.ERROR}
                                    >
                                        {t(periodErrorKey)}
                                    </Text>
                                </div>
                            )}
                        </div>

                        <IconPicker
                            selectedIcon={selectedIcon}
                            onIconChange={(iconId) => {
                                setSelectedIcon(iconId);
                                setValue('icon', iconId || '');
                            }}
                        />

                        <ColorPicker
                            selectedColor={selectedColor}
                            onColorChange={setSelectedColor}
                            defaultColor={'#0DD3C3'}
                            onReset={() => setValue('iconBackground', '')}
                        />
                    </div>
                ) : (
                    <ValueTranslationsTab
                        valueId={null}
                        questions={[]}
                        configuredLanguages={configuredLanguages}
                        currentLanguage={currentLanguage}
                        initialTranslations={translations}
                        onTranslationsChange={setTranslations}
                        onValidationChange={setTranslationsValid}
                        nameLabel={'pillars.nameLabel'}
                        descriptionLabel={'pillars.descriptionLabel'}
                        nameMaxLength={NAME_MAX_LENGTH}
                        descriptionMaxLength={DESCRIPTION_MAX_LENGTH}
                        nameMaxLengthMessage={'pillars.nameMaxLength'}
                        descriptionMaxLengthMessage={'pillars.descriptionMaxLength'}
                    />
                )}
            </ModalDialog>

            <PillarLeaveActiveDialog
                isOpen={isDeactivateDialogOpen}
                pillar={pillar ?? null}
                targetStatus={
                    pendingSaveData?.status === PillarStatus.DRAFT
                        ? PillarStatus.DRAFT
                        : PillarStatus.INACTIVE
                }
                isConfirming={isSaving}
                onCancel={handleCancelDeactivate}
                onConfirm={handleConfirmDeactivate}
            />
        </>
    );
}
